/**
 * Custom chart tooltip: shows Range A and Range B values (each with its real bucket date)
 * and their Δ, tinted by the metric's delta sense.
 * For Media Devoted | Created By Ciro Autiero
 */

import { cn } from '@/lib/utils';

import type { CompareMetric } from '../../metrics/compareMetrics';
import { formatMetricDelta, formatMetricValue } from '../../metrics/metricFormat';
import { deltaTone, DELTA_TONE_TEXT, metricStroke } from '../../metrics/metricColor';
import type { ChartRow } from './compareChartSeries';

interface Props {
  active?: boolean;
  payload?: Array<{ payload?: ChartRow }>;
  metric: CompareMetric;
  currency: string;
  labelA: string;
  labelB: string;
}

function TipLine({ dashed, name, value, metric, currency }: {
  dashed: boolean;
  name: string;
  value: number | null;
  metric: CompareMetric;
  currency: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <span
          className="inline-block h-0 w-3 border-t-2"
          style={{ borderColor: metricStroke(metric), borderStyle: dashed ? 'dashed' : 'solid' }}
        />
        {name}
      </span>
      <span className="font-medium tabular-nums text-foreground">
        {value == null ? '—' : formatMetricValue(metric, value, currency)}
      </span>
    </div>
  );
}

export function CompareChartTooltip({ active, payload, metric, currency, labelA, labelB }: Props) {
  const row = active ? payload?.[0]?.payload : undefined;
  if (!row) return null;
  const both = typeof row.a === 'number' && typeof row.b === 'number';
  // A = current, B = baseline; Δ = A − B (matches the diff table + legend).
  const delta = both ? (row.a as number) - (row.b as number) : 0;
  const tone = deltaTone(metric, delta);

  return (
    <div className="min-w-[9rem] rounded-md border bg-popover px-2.5 py-2 text-xs shadow-md">
      <div className="mb-1 font-medium text-foreground">{metric.label}</div>
      <TipLine dashed={false} name={`${labelA}${row.labelA ? ` · ${row.labelA}` : ''}`} value={row.a} metric={metric} currency={currency} />
      <TipLine dashed name={`${labelB}${row.labelB ? ` · ${row.labelB}` : ''}`} value={row.b} metric={metric} currency={currency} />
      {both && (
        <div className={cn('mt-1 border-t border-border/60 pt-1 text-right font-medium', DELTA_TONE_TEXT[tone])}>
          Δ {formatMetricDelta(metric, delta, currency)}
        </div>
      )}
    </div>
  );
}

export default CompareChartTooltip;
